import { defineStore } from 'pinia'
import { ref } from 'vue'
import { acknowledgeAiUnreadCardShown, clearTabRedDot, consumeAiUnreadCard, getTabRedDots, getUnshownAiUnreadCard, type TabCode } from '@/api/tabRedDot'
import { applyClearResult, createEmptyTabRedDots, isTabRedDotResponseCurrent, normalizeTabRedDots } from './tabRedDotPolicy'
import { isLogin } from '@/utils/auth'

export const useTabRedDotStore = defineStore('tab-red-dot', () => {
  const redDots = ref(createEmptyTabRedDots())
  const loading = ref(false)
  const clearing = ref<Partial<Record<TabCode, boolean>>>({})
  let requestGeneration = 0
  let stateRevision = 0

  const reset = () => {
    requestGeneration += 1
    stateRevision += 1
    redDots.value = createEmptyTabRedDots()
    loading.value = false
    clearing.value = {}
  }

  const getAuthenticatedGeneration = async () => {
    const generation = requestGeneration
    if (await isLogin()) return generation
    reset()
    return null
  }

  const fetchRedDots = async () => {
    if (loading.value) return redDots.value
    const generation = await getAuthenticatedGeneration()
    if (generation === null) return redDots.value
    const revision = stateRevision
    loading.value = true
    try {
      const result = normalizeTabRedDots((await getTabRedDots()) || [])
      if (generation === requestGeneration && isTabRedDotResponseCurrent(revision, stateRevision)) {
        redDots.value = result
      }
      return redDots.value
    } finally {
      if (generation === requestGeneration) loading.value = false
    }
  }

  const clear = async (tabCode: TabCode) => {
    const generation = await getAuthenticatedGeneration()
    if (generation === null) return false
    if (!redDots.value[tabCode]) return true
    if (clearing.value[tabCode]) return false
    clearing.value[tabCode] = true
    try {
      const cleared = await clearTabRedDot(tabCode)
      if (generation === requestGeneration) {
        if (cleared === true) stateRevision += 1
        redDots.value[tabCode] = applyClearResult(redDots.value[tabCode], cleared === true)
      }
      return cleared === true
    } finally {
      if (generation === requestGeneration) clearing.value[tabCode] = false
    }
  }

  const consumeAiTarget = async () => {
    const generation = await getAuthenticatedGeneration()
    if (generation === null) return null
    const target = await consumeAiUnreadCard()
    return generation === requestGeneration ? target : null
  }

  const getUnshownAiTarget = async () => {
    const generation = await getAuthenticatedGeneration()
    if (generation === null) return null
    const target = await getUnshownAiUnreadCard()
    return generation === requestGeneration ? target : null
  }

  const acknowledgeAiTargetShown = async (contentId: string) => {
    const generation = await getAuthenticatedGeneration()
    if (generation === null) return false
    const acknowledged = await acknowledgeAiUnreadCardShown(contentId)
    return generation === requestGeneration && acknowledged === true
  }

  return { redDots, loading, clearing, fetchRedDots, clear, consumeAiTarget, getUnshownAiTarget, acknowledgeAiTargetShown, reset }
})
